import {
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from "@angular/forms";
import { NzDatePickerModule } from "ng-zorro-antd/date-picker";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzI18nService } from "ng-zorro-antd/i18n";
import { DEFAULT_DATETIME_TIME } from "../../../constants/app-constants";

@Component({
  selector: "app-date-range-picker",
  standalone: true,
  imports: [NzDatePickerModule, NzFormModule, NzIconModule, NzInputModule, FormsModule],
  templateUrl: "./date-range-picker.component.html",
  styleUrl: "./date-range-picker.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    }
  ]
})
export class DateRangePickerComponent implements ControlValueAccessor {
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;

  @Input() showTime: boolean = true;
  @Input() format: string = this.getDefaultDateFormat();
  @Input() timeFormat: string = this.getDefaultTimeFormat();

  @Input() status: "error" | "warning" | "" = "";
  @Input() use12HourFormat: boolean = true;

  @Input() nzDisabledDate?: (current: Date) => boolean;

  @Output() dateRangeChange = new EventEmitter<{ from: string, to: string } | null>();
  @Output() onOpenChange = new EventEmitter<boolean>();

  ngControl: NgControl | null = null;
  value: [Date, Date] | null = null;
  isOpen: boolean = false;

  onChange: any = () => {
  };
  onTouched: any = () => {
  };

  constructor(private injector: Injector, private i18n: NzI18nService) {
    this.format = this.getDefaultDateFormat();
    this.timeFormat = this.getDefaultTimeFormat();
  }

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: any): void {
    // Handle object format {from: string, to: string} or Date arrays
    if (value && typeof value === "object" && value.from && value.to) {
      // Parse object format like {from: "2023-12-25", to: "2023-12-30"}
      const fromDate = this.parseDateValue(value.from);
      const toDate = this.parseDateValue(value.to);
      if (fromDate && toDate) {
        this.value = [fromDate, toDate];
      } else {
        this.value = null;
      }
    } else if (Array.isArray(value) && value.length === 2) {
      // Handle Date array values
      this.value = [value[0], value[1]] as [Date, Date];
    } else {
      this.value = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateRangeChange(dateRange: [Date, Date] | null): void {
    this.value = dateRange;

    // Format the date range values as object with from/to properties
    const formattedRange = this.formatDateRange(dateRange);
    this.onChange(formattedRange);
    this.onTouched();
    this.dateRangeChange.emit(formattedRange);
  }

  onOpenChangeHandler(open: boolean): void {
    this.isOpen = open;
    this.onOpenChange.emit(open);
  }

  hasError(): boolean {
    return !!(this.ngControl?.control?.invalid && this.ngControl?.control?.touched && this.ngControl?.control?.errors);
  }

  getStatus(): "error" | "warning" | "" {
    if (this.hasError()) {
      return "error";
    }
    return this.status;
  }

  getShowTimeConfig(): any {
    if (!this.showTime) {
      return null;
    }

    // Create default time Date objects with current date but time set to 8:00 AM
    const defaultStartTime = new Date();
    defaultStartTime.setHours(DEFAULT_DATETIME_TIME.hour, DEFAULT_DATETIME_TIME.minute, DEFAULT_DATETIME_TIME.second, 0);

    return {
      nzFormat: this.timeFormat,
      nzUse12Hours: this.use12HourFormat,
      nzDefaultOpenValue: defaultStartTime
    };
  }

  getFormat(): string {
    if (this.showTime) {
      return `${this.format} ${this.timeFormat}`;
    }
    return this.format;
  }

  private getDefaultDateFormat(): string {
    const locale = this.getUserLocale();

    // Create a sample date to test locale formatting
    const sampleDate = new Date(2023, 11, 25); // December 25, 2023

    // Get the locale-specific date format
    const dateFormatter = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });

    const parts = dateFormatter.formatToParts(sampleDate);
    let format = "";

    parts.forEach(part => {
      switch (part.type) {
        case "year":
          format += "yyyy";
          break;
        case "month":
          format += "MM";
          break;
        case "day":
          format += "dd";
          break;
        case "literal":
          format += part.value;
          break;
      }
    });

    return format || "dd/MM/yyyy"; // Fallback to default
  }

  private getDefaultTimeFormat(): string {
    const locale = this.getUserLocale();

    // Create a sample time to test locale formatting
    const sampleTime = new Date(2023, 0, 1, 14, 30, 45); // 2:30:45 PM

    // Get the locale-specific time format
    const timeFormatter = new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: this.use12HourFormat // Use 12-hour format if requested
    });

    const parts = timeFormatter.formatToParts(sampleTime);
    let format = "";

    parts.forEach(part => {
      switch (part.type) {
        case "hour":
          format += this.use12HourFormat ? "hh" : "HH";
          break;
        case "minute":
          format += "mm";
          break;
        case "second":
          format += "ss";
          break;
        case "literal":
          format += part.value;
          break;
      }
    });

    // Add AM/PM format if using 12-hour format
    if (this.use12HourFormat) {
      format += " a";
    }

    return format || (this.use12HourFormat ? "hh:mm:ss a" : "HH:mm:ss"); // Fallback to default
  }

  private getUserLocale(): string {
    // Get the user's preferred locale
    const locale = navigator.language || navigator.languages?.[0] || "en-US";

    // Return the locale or fallback to en-US
    return locale || "en-US";
  }

  /**
   * Format date range values as object with from/to properties
   * Returns an object with formatted date strings
   */
  private formatDateRange(dateRange: [Date, Date] | null): { from: string, to: string } | null {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return null;
    }

    const fromDate = this.getDateString(dateRange[0]);
    const toDate = this.getDateString(dateRange[1]);

    return {
      from: fromDate,
      to: toDate
    };
  }

  /**
   * Format a single date to string similar to the existing date-picker component
   */
  private getDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    if (this.showTime) {
      // For datetime-local format with timezone offset
      const offset = -date.getTimezoneOffset();
      const sign = offset >= 0 ? "+" : "-";
      const absOffset = Math.abs(offset);
      const offsetHours = Math.floor(absOffset / 60)
        .toString()
        .padStart(2, "0");
      const offsetMinutes = (absOffset % 60).toString().padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
    } else {
      // For date format
      return `${year}-${month}-${day}`;
    }
  }

  /**
   * Parse date value similar to the existing date-picker component
   */
  private parseDateValue(value: string | number): Date | null {
    if (!value) return null;
    if (typeof value === "number") {
      return new Date(value);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  protected readonly Date = Date;
}
